import { Sparkles, Zap, Wrench, Bug } from 'lucide-react'
import { changelog, type ChangelogEntry } from '../../data/changelog'
import { Badge } from '../../components/ui/Badge'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'

const categoryConfig: Record<
  ChangelogEntry['categories'][number]['type'],
  { label: string; icon: React.ComponentType<{ className?: string }>; variant: 'default' | 'success' | 'warning' | 'info' }
> = {
  feature: { label: 'New', icon: Sparkles, variant: 'default' },
  improvement: { label: 'Improved', icon: Zap, variant: 'info' },
  fix: { label: 'Fixed', icon: Bug, variant: 'warning' },
}

function CategorySection({ category }: { category: ChangelogEntry['categories'][number] }) {
  const config = categoryConfig[category.type]
  const Icon = config.icon

  return (
    <div className="space-y-2">
      <Badge variant={config.variant} size="sm" className="gap-1">
        <Icon className="size-3" />
        {config.label}
      </Badge>
      <ul className="ml-4 space-y-1">
        {category.items.map((item, i) => (
          <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
            <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  )
}

export default function ChangelogPage() {
  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">What's New</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Latest updates and improvements to CleverAct AI.
        </p>
      </div>

      <div className="relative space-y-6">
        {/* Timeline line */}
        <div className="absolute left-[15px] top-2 bottom-2 w-px bg-border" />

        {changelog.map((entry) => (
          <div key={entry.version} className="relative pl-10">
            {/* Timeline dot */}
            <div className="absolute left-2 top-6 size-3 rounded-full border-2 border-primary bg-background" />

            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{entry.title}</CardTitle>
                  <Badge variant="outline" size="sm">
                    v{entry.version}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">{entry.date}</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {entry.categories.map((cat, i) => (
                  <CategorySection key={i} category={cat} />
                ))}
              </CardContent>
            </Card>
          </div>
        ))}
      </div>
    </div>
  )
}
